<template>
  <div>
    <base-drawer
      :show="show"
      :title="drawerView === 'detail' ? 'Waiver details' : (isCopyMode ? 'Copy waiver to booking slot' : (isWalkinWaiver ? 'Assign waiver to booking' : 'Move waiver to booking slot'))"
      width="560px"
      @close="closeDrawer">

      <!-- DETAIL VIEW -->
      <div v-if="drawerView === 'detail' && drawerLoading">
        <div class="skel skel--text mb-2" style="width: 60%"></div>
        <div class="skel skel--text mb-3" style="width: 40%"></div>
        <div class="skel skel--text mb-2" style="width: 50%"></div>
        <div class="skel skel--text" style="width: 70%"></div>
      </div>

      <div v-else-if="drawerView === 'detail' && selectedWaiver">
        <drawer-section title="Customer" icon="icon-single-02">
          <template v-if="showCustomerLink" #header-actions>
            <a class="drawer-action-link"
               @click.stop.prevent="viewCustomerFromDrawer"
               href="#">
              View details <i class="tim-icons el-icon-top-right"></i>
            </a>
          </template>
          <p class="text-white mb-1">
            {{ selectedWaiver.display_first_name || selectedWaiver.first_name }}
            {{ selectedWaiver.display_last_name || selectedWaiver.last_name }}
            <badge v-if="selectedWaiver.is_minor" type="primary" class="ml-1">minor</badge>
            <badge v-if="!!selectedWaiver.created_internally" type="info" class="ml-1">created by staff</badge>
          </p>
          <p v-if="selectedWaiver.email" class="mb-1 small text-white">
            <i class="tim-icons icon-email-85 mr-1"></i> {{ selectedWaiver.email }}
          </p>
          <p v-if="selectedWaiver.display_phone || selectedWaiver.phone" class="mb-1 small text-white">
            <i class="tim-icons icon-mobile mr-1"></i> {{ formatPhone(selectedWaiver.display_phone || selectedWaiver.phone) }}
          </p>
          <p v-if="isValidDate(selectedWaiver.birthday)" class="mb-1 small text-white">
            <i class="tim-icons icon-gift-2 mr-1"></i> {{ getDate(selectedWaiver.birthday) }}
          </p>
          <p v-if="(selectedWaiver.location_city && selectedWaiver.location_city.length) || (selectedWaiver.location_state && selectedWaiver.location_state.length) || (selectedWaiver.location_zip && selectedWaiver.location_zip.length)"
             class="mb-1 small text-white">
            <i class="tim-icons icon-square-pin mr-1"></i>
            <span v-if="selectedWaiver.location_city">{{ selectedWaiver.location_city }}</span><span v-if="selectedWaiver.location_city && selectedWaiver.location_state">, </span><span v-if="selectedWaiver.location_state">{{ selectedWaiver.location_state }}</span><span v-if="(selectedWaiver.location_city || selectedWaiver.location_state) && selectedWaiver.location_zip">&nbsp;</span><span v-if="selectedWaiver.location_zip">{{ selectedWaiver.location_zip }}</span>
          </p>
        </drawer-section>

        <drawer-section title="Event" icon="icon-shape-star">
          <template v-if="selectedWaiver.game_instance_id && hasPermission('game_history_view')" #header-actions>
            <a class="drawer-action-link"
               @click.stop.prevent="viewEventFromDrawer"
               href="#">
              View details <i class="tim-icons el-icon-top-right"></i>
            </a>
          </template>
          <p class="text-white mb-1">{{ selectedWaiver.game_name || selectedWaiver.game || 'Walk-in' }}</p>
          <p v-if="selectedWaiver.booking_slot_id && selectedWaiver.booking_date" class="mb-2 small text-white">
            {{ getDate(selectedWaiver.booking_date) }} <span v-if="selectedWaiver.start_time">{{ Number(selectedWaiver.is_all_day) === 1 ? '' : 'at ' }}{{ getDisplayTime(selectedWaiver) }}</span>
          </p>
          <div v-if="canEdit" class="event-actions">
            <base-button v-if="isWalkinWaiver" variant="outlined" tone="primary" @click="moveWaiverFromDrawer">
              <i class="tim-icons el-icon-d-arrow-right"></i>
              <span>Assign to booking</span>
            </base-button>
            <template v-else>
              <base-button variant="outlined" tone="primary" @click="moveWaiverFromDrawer">
                <i class="tim-icons el-icon-d-arrow-right"></i>
                <span>Move</span>
              </base-button>
              <base-button variant="outlined" tone="primary" @click="copyWaiverFromDrawer">
                <i class="tim-icons icon-single-copy-04"></i>
                <span>Copy</span>
              </base-button>
              <base-button v-if="canClearBooking" variant="outlined" tone="primary" @click="clearPrompt.show = true">
                <i class="tim-icons icon-simple-remove"></i>
                <span>Clear booking</span>
              </base-button>
            </template>
          </div>
        </drawer-section>

        <drawer-section title="Settings" icon="icon-settings-gear-63">
          <div class="d-flex align-items-center" style="gap: 6px;">
            <el-switch active-color="#e14eca" v-model="selectedWaiver.not_participating" :disabled="!canEdit" @change="toggleNotParticipating" />
            <label class="mb-0 text-white" style="font-size: 13px;">Not participating in the event</label>
          </div>
        </drawer-section>

        <drawer-section v-if="selectedWaiver.created_time || selectedWaiver.signed_date || !!selectedWaiver.has_signature || (selectedWaiver.experience && selectedWaiver.experience.length) || (selectedWaiver.source && selectedWaiver.source.length) || !!selectedWaiver.survey_email_sent || (!selectedWaiver.is_minor && (hasConsentField('Email Opt Out') || hasConsentField('Photo Opt Out')))"
                        title="Waiver record" icon="icon-tag">
          <template v-if="!!selectedWaiver.created_time || !!selectedWaiver.has_signature" #header-actions>
            <a class="drawer-action-link pdf-download-link"
               :class="{ 'is-busy': pdfGenerating }"
               href="#" @click.stop.prevent="downloadWaiverPdf">
              <i :class="pdfGenerating ? 'el-icon-loading' : 'tim-icons icon-cloud-download-93'"></i>
              <span>{{ pdfGenerating ? 'Generating…' : 'Download PDF' }}</span>
            </a>
          </template>
          <p v-if="selectedWaiver.created_time" class="mb-1 small text-white">
            <i class="tim-icons icon-calendar-60 mr-1"></i>
            Signed on {{ getSignedDate(selectedWaiver.created_time) }} <span v-if="getSignedTime(selectedWaiver.created_time)">at {{ getSignedTime(selectedWaiver.created_time) }}</span>
          </p>
          <div v-if="!!selectedWaiver.has_signature || !!selectedWaiver.not_participating" class="signature-block">
            <p class="signature-label text-white"><i class="tim-icons icon-pencil mr-1"></i>Signature</p>
            <waiver-signature
              :key="selectedWaiver.id"
              size="detail"
              :waiver-id="selectedWaiver.id"
              :signature="selectedWaiver.signature"
              :has-signature="!!selectedWaiver.has_signature"
              :is-minor="!!selectedWaiver.is_minor"
              :not-participating="!!selectedWaiver.not_participating"
            />
          </div>
          <p v-if="selectedWaiver.experience && selectedWaiver.experience.length" class="mb-1 small text-white">
            <i class="tim-icons icon-trophy mr-1"></i>
            Experience: {{ selectedWaiver.experience }}
          </p>
          <p v-if="selectedWaiver.source && selectedWaiver.source.length" class="mb-1 small text-white">
            <i class="tim-icons icon-link-72 mr-1"></i>
            Source: {{ selectedWaiver.source }}
          </p>
          <p v-if="!!selectedWaiver.survey_email_sent" class="mb-1 small text-white">
            <i class="tim-icons icon-email-85 mr-1"></i>
            Post-event survey sent
          </p>
          <div v-if="!selectedWaiver.is_minor && (hasConsentField('Email Opt Out') || hasConsentField('Photo Opt Out'))" class="mt-2">
            <p v-if="hasConsentField('Email Opt Out')" class="mb-1 small text-white">
              <badge :type="!!selectedWaiver.email_optout ? 'danger' : 'success'" class="mr-2">
                {{ getConsentBadgeLabel('Email Opt Out') }}
              </badge>
              <span class="consent-question">"{{ getConsentLabel('Email Opt Out') }}"</span>
            </p>
            <p v-if="hasConsentField('Photo Opt Out')" class="mb-0 small text-white">
              <badge :type="!!selectedWaiver.picture_optout ? 'danger' : 'success'" class="mr-2">
                {{ getConsentBadgeLabel('Photo Opt Out') }}
              </badge>
              <span class="consent-question">"{{ getConsentLabel('Photo Opt Out') }}"</span>
            </p>
          </div>
        </drawer-section>

        <drawer-section v-if="customFieldResponses.length" title="Custom fields" icon="icon-bullet-list-67">
          <template #header-actions>
            <span class="cf-count-badge mr-2">{{ customFieldResponses.length }}</span>
            <el-dropdown trigger="click" @command="copyCustomFields" @click.native.stop>
              <span class="cf-copy-link">
                <i class="tim-icons icon-single-copy-04" style="font-size: 10px;"></i> Copy
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="text">Copy as text</el-dropdown-item>
                <el-dropdown-item command="csv">Copy as CSV</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
          <div class="custom-fields-list">
            <div v-for="r in customFieldResponses" :key="'CFR' + r.id" class="custom-field-item">
              <span class="cf-label">
                {{ r.label }}
                <el-tooltip v-if="r.archived"
                            content="This field is no longer collected on new waivers"
                            effect="light" :open-delay="300" placement="top">
                  <badge type="default" class="ml-1">archived</badge>
                </el-tooltip>
              </span>
              <span class="cf-value text-white">{{ formatCustomFieldResponse(r) }}</span>
              <span v-if="r.source" class="cf-meta">
                <span class="cf-meta-label">Source:</span>
                <custom-field-source-badge :source="r.source" />
              </span>
            </div>
          </div>
        </drawer-section>

        <drawer-section v-if="canDelete"
                        title="Delete waiver" icon="icon-trash-simple" variant="danger">
          <div class="danger-action-row">
            <span class="danger-action-hint">Permanently remove this waiver.</span>
            <base-button variant="outlined" tone="danger" @click="deletePrompt.show = true">
              <i class="tim-icons icon-trash-simple"></i>
              <span>Delete</span>
            </base-button>
          </div>
        </drawer-section>
      </div>

      <!-- BOOKING SELECTOR VIEW (Move / Copy) -->
      <div v-else-if="drawerView === 'booking-selector'">
        <a class="drawer-back-link mb-3" @click.prevent="backToDetail" href="#">
          <i class="tim-icons icon-minimal-left mr-1"></i> Back to waiver
        </a>

        <base-date-navigator v-model="current" class="mb-3" @change="changeDate()" />

        <drawer-section title="Bookings" icon="icon-calendar-60">
          <div v-if="bookingsLoading">
            <div v-for="n in 4" :key="'bsk' + n" class="d-flex align-items-center mb-2" style="gap: 12px">
              <span class="skel skel--dot"></span>
              <div style="flex: 1">
                <div class="skel" style="width: 60%; height: 14px; margin-bottom: 6px"></div>
                <div class="skel skel--line-sm"></div>
              </div>
              <span class="skel skel--badge"></span>
            </div>
          </div>
          <div v-else-if="emptyBooking || filteredBookings.length === 0 && !drawerShowAllBookings" class="text-center py-3">
            <span style="font-size: 2em"><i class="tim-icons icon-calendar-60"></i></span>
            <h4 class="mt-2 text-white" style="font-size: 14px">
              <span v-if="emptyBooking">No bookings on this day</span>
              <span v-else>No booked or completed slots on this day</span>
            </h4>
          </div>
          <div v-else class="bsel-picker-list">
            <div v-for="(b, i) in filteredBookings"
                 :key="'BSEL' + i"
                 class="bsel-picker-row"
                 :class="{'bsel-picker-row--selected': selectedBooking && selectedBooking.id === b.id}"
                 @click="selectedBooking = b">
              <div class="bsel-picker-row__time">
                <span>{{getDisplayTime(b)}}</span>
              </div>
              <div class="bsel-picker-event">
                <div class="bsel-picker-event__thumb-wrap" :class="{'is-selected': selectedBooking && selectedBooking.id === b.id}">
                  <img v-if="b.game_img" v-twin-src="b.game_img" class="bsel-picker-event__img" alt="" />
                  <div v-else class="bsel-picker-event__img bsel-picker-event__img--placeholder"></div>
                  <div class="bsel-picker-event__check" :class="{'is-checked': selectedBooking && selectedBooking.id === b.id}">
                    <i class="tim-icons icon-check-2"></i>
                  </div>
                </div>
                <div class="bsel-picker-event__text">
                  <div class="text-white" style="font-weight: 600">{{b.game_name}}</div>
                  <small class="text-white" style="opacity: 0.7">{{getDate(b.booking_date)}}</small>
                </div>
                <div class="bsel-picker-event__status">
                  <badge v-if="b.status == 'available'" type="success">available</badge>
                  <badge v-else-if="b.status == 'booked'" type="info">{{b.status}}</badge>
                  <badge v-else-if="b.status == 'completed'" type="warning">{{b.status}}</badge>
                  <badge v-else type="default">{{b.status}}</badge>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!bookingsLoading && !emptyBooking && (drawerShowAllBookings || hiddenBookingsCount > 0)" class="bsel-show-all">
            <button type="button" class="bsel-show-all__btn" @click="drawerShowAllBookings = !drawerShowAllBookings">
              <span v-if="!drawerShowAllBookings">Show {{hiddenBookingsCount}} other slots</span>
              <span v-else>Hide unbooked slots</span>
            </button>
          </div>
        </drawer-section>
      </div>

      <template v-if="drawerView === 'booking-selector'" #footer>
        <div class="d-flex justify-content-end" style="gap: 10px;">
          <base-button size="sm" type="default" @click="backToDetail">Cancel</base-button>
          <base-button size="sm" type="primary" :disabled="!selectedBooking || saving" @click="updateWaiver()">
            <i class="tim-icons mr-1" :class="isCopyMode ? 'icon-single-copy-04' : 'el-icon-d-arrow-right'"></i>
            {{ isCopyMode ? 'Copy waiver' : (isWalkinWaiver ? 'Assign waiver' : 'Move waiver') }}
          </base-button>
        </div>
      </template>
    </base-drawer>

    <base-alert-dialog
      :show="clearPrompt.show"
      title="Clear booking?"
      message="This waiver is removed from the booking and becomes a walk-in."
      variant="warning"
      show-cancel
      confirm-text="Clear booking"
      :loading="clearPrompt.loading"
      :dismissible="!clearPrompt.loading"
      @confirm="clearWaiverBooking"
      @cancel="clearPrompt.show = false" />

    <base-alert-dialog
      :show="deletePrompt.show"
      title="Delete waiver?"
      message="The waiver is permanently deleted. This cannot be undone."
      variant="danger"
      show-cancel
      confirm-text="Delete waiver"
      confirm-variant="danger"
      :loading="deletePrompt.loading"
      :dismissible="!deletePrompt.loading"
      @confirm="deleteWaiver"
      @cancel="deletePrompt.show = false" />

    <!-- Off-screen branded PDF renderer; generate() is called from Download PDF -->
    <waiver-pdf-document ref="waiverPdf" />
  </div>
</template>

<script setup lang="ts">
// Waiver detail drawer shared by Waivers > History and the customer page Waivers tab.
// The parent owns `show` and passes the clicked list row as `waiver`; the drawer shows
// it at once, then merges in the full detail (signature, custom fields, consent).
// Move, Copy, Clear booking and Delete report back through `changed` / `deleted` so
// each page refreshes its own list.
import { ref, reactive, computed, watch, nextTick, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import moment from 'moment'
import { Auth } from 'aws-amplify'
import Badge from '@/components/Badge'
import { API_LOCATION } from '@/api-config'
import { fetchWaiverDetail } from '@/util/waiverSignature'
import { displayTime, formattedDate } from '@/plugins/dateFormatter'
import { formatGroupDate, formatGroupTime, isValidDate } from '@/plugins/dates'
import { formatPhoneDisplay } from '@/util/phoneFormatter'

// No `| null` in these types: Vue 2.7 compiles it to `type: [..., null]`, which Vue 2 rejects
// ("null is not a constructor"). Optional props already accept null from the parent.
const props = withDefaults(defineProps<{
  show: boolean
  waiver?: Record<string, any>
  currentCustomerId?: number | string
  readOnly?: boolean
}>(), {
  readOnly: false,
})

type ChangeAction = 'moved' | 'assigned' | 'copied' | 'cleared' | 'participation'
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'changed', payload: { action: ChangeAction; waiverId: number; notParticipating?: boolean }): void
  (e: 'deleted', waiverId: number): void
}>()

const { proxy } = getCurrentInstance() as any
const group = JSON.parse(localStorage.getItem('group') || '{}')
const dateFormat = group.date_format
const timeFormat = group.time_format

const selectedWaiver = ref<any>(null)
const selectedBooking = ref<any>('')
const bookings = ref<any[]>([])
const emptyBooking = ref(false)
const current = ref('')
const customFieldResponses = ref<any[]>([])
const consentFields = ref<any[]>([])
const pdfGenerating = ref(false)
const drawerLoading = ref(false)
const drawerView = ref<'detail' | 'booking-selector'>('detail')
const isCopyMode = ref(false)
const bookingsLoading = ref(false)
const drawerShowAllBookings = ref(false)
const saving = ref(false)
const clearPrompt = reactive({ show: false, loading: false })
const deletePrompt = reactive({ show: false, loading: false })
const waiverPdf = ref<any>(null)

function hasPermission(scope: string): boolean {
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
  return permissions.some((p: string) => p === scope)
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function groupUrl(): string {
  return API_LOCATION + 'companies/' + localStorage.getItem('companyId') + '/groups/' + group.id
}

function waiverUrl(): string {
  return groupUrl() + '/waivers/' + selectedWaiver.value.id
}

// A walk-in has no event and no booking slot.
const isWalkinWaiver = computed(() => {
  const w = selectedWaiver.value
  return !!w && !w.booking_slot_id && !w.game_id
})

// Mirrors the lambda's clear gates so the button only shows when the clear will be accepted:
// on a booking that is not completed, signed on a waiver template in this group.
const canClearBooking = computed(() => {
  const w = selectedWaiver.value
  if (!w || !w.booking_slot_id) return false
  if (w.booking_status === 'completed') return false
  return !!w.waiver_configuration_id && Number(w.template_group_id) === Number(group.id)
})

// Bookings filtered to only "booked" or "completed" by default. When the user
// expands ("Show N other slots"), all bookings are shown.
const filteredBookings = computed(() => {
  if (!bookings.value || bookings.value.length === 0) return []
  if (drawerShowAllBookings.value) return bookings.value
  return bookings.value.filter(b => b.status === 'booked' || b.status === 'completed')
})

const hiddenBookingsCount = computed(() => {
  if (!bookings.value || bookings.value.length === 0) return 0
  return bookings.value.filter(b => b.status !== 'booked' && b.status !== 'completed').length
})

const canEdit = computed(() => !props.readOnly && hasPermission('waivers_update'))

// Mirrors the delete lambda's gate: a waiver on a completed booking is kept.
const canDelete = computed(() => !props.readOnly && hasPermission('waivers_delete') &&
  !!selectedWaiver.value && selectedWaiver.value.booking_status !== 'completed')

// On the customer page the link would point at the page already open.
const showCustomerLink = computed(() => {
  const w = selectedWaiver.value
  if (!hasPermission('customer_view') || !w || !w.user_id) return false
  return props.currentCustomerId == null || Number(w.user_id) !== Number(props.currentCustomerId)
})

function getDate(d: any) {
  return formattedDate(dateFormat, d)
}

function getDisplayTime(slot: any) {
  // Walk-in waivers have no slot time. Show a dash rather than a formatted empty time.
  if (slot && !slot.start_time && Number(slot.is_all_day) !== 1) {
    return '-'
  }
  return displayTime(slot, timeFormat)
}

function getSignedDate(d: any) {
  return formatGroupDate(d, { dayOfWeek: false })
}

function getSignedTime(d: any) {
  return formatGroupTime(d)
}

function formatPhone(phone: string) {
  return formatPhoneDisplay(phone)
}

function getConsentLabel(name: string) {
  const field = Array.isArray(consentFields.value)
    ? consentFields.value.find(f => f.name === name)
    : null
  if (field && field.label) return field.label
  const isOptin = field ? !!field.is_optin : false
  if (name === 'Email Opt Out') {
    return isOptin ? 'Yes, sign me up for marketing emails' : 'Do not include me in mailing lists'
  }
  if (name === 'Photo Opt Out') {
    return isOptin ? 'I consent to my photos being shared on social media' : 'Do not share my photos on social media'
  }
  return ''
}

function hasConsentField(name: string) {
  return Array.isArray(consentFields.value) && consentFields.value.some(f => f.name === name)
}

function getConsentBadgeLabel(name: string) {
  const field = Array.isArray(consentFields.value)
    ? consentFields.value.find(f => f.name === name)
    : null
  const isOptIn = field ? !!field.is_optin : false
  const optoutValue = name === 'Email Opt Out'
    ? !!selectedWaiver.value.email_optout
    : !!selectedWaiver.value.picture_optout
  // The customer's literal checkbox state. Opt-in mode + box checked = consent.
  // Opt-out mode + box checked = refusal. XOR captures both.
  const ticked = isOptIn !== optoutValue
  const subject = name === 'Email Opt Out' ? 'Email' : 'Photo'
  const mode = isOptIn ? 'opt-in' : 'opt-out'
  return `${subject} ${mode}: ${ticked ? 'yes' : 'no'}`
}

function formatCustomFieldResponse(response: any) {
  if (response.type === 1) {
    return response.response === '1' || response.response === 1 ? 'Yes' : 'No'
  }
  return response.response || 'Not provided'
}

function copyCustomFields(format: string) {
  const fields = customFieldResponses.value
  if (!fields || !fields.length) return
  let text
  if (format === 'csv') {
    const header = 'Field,Response'
    const rows = fields.map(f => {
      const label = (f.label || '').replace(/"/g, '""')
      const value = (formatCustomFieldResponse(f) || '').replace(/"/g, '""')
      return `"${label}","${value}"`
    })
    text = [header, ...rows].join('\n')
  } else {
    text = fields.map(f => `${f.label}: ${formatCustomFieldResponse(f)}`).join('\n')
  }
  navigator.clipboard.writeText(text).then(() => {
    proxy.$notify({ type: 'success', message: 'Copied to clipboard' })
  }).catch(() => {
    proxy.$notify({ type: 'danger', message: 'Could not copy. Please try again.' })
  })
}

async function loadWaiverDetail(id: number) {
  drawerLoading.value = true
  try {
    // includeSignature pulls the signature (decoded data-URL) + waiver_text in the
    // same round trip, so the in-drawer signature renders and Download PDF has
    // everything it needs without a second fetch.
    const { waiver, custom_field_responses, consent_fields } =
      await fetchWaiverDetail(id, { includeSignature: true })
    // Merge: keep list-provided fields (game_instance_id, img) and overlay detail fields.
    // Preserve not_participating value the user may have toggled before fetch resolved.
    const userToggled = selectedWaiver.value && selectedWaiver.value.id === id
      ? selectedWaiver.value.not_participating
      : null
    selectedWaiver.value = { ...selectedWaiver.value, ...waiver }
    // Cast tinyint(1) to true boolean for el-switch v-model
    selectedWaiver.value.not_participating = userToggled !== null
      ? !!userToggled
      : !!selectedWaiver.value.not_participating
    customFieldResponses.value = custom_field_responses || []
    consentFields.value = consent_fields || []
  } catch (err: any) {
    console.log(err)
    proxy.$notify({
      type: 'danger',
      message: err && err.response && err.response.status === 401
        ? 'You do not have access to this action.'
        : 'Could not load waiver details. Please try again.',
    })
  } finally {
    drawerLoading.value = false
  }
}

async function downloadWaiverPdf() {
  if (!selectedWaiver.value || pdfGenerating.value) return
  // selectedWaiver already carries the decoded signature + waiver_text from
  // loadWaiverDetail's include=signature fetch, so the PDF is fully populated.
  pdfGenerating.value = true
  try {
    await waiverPdf.value.generate({
      waiver: selectedWaiver.value,
      consent_fields: consentFields.value,
      custom_field_responses: customFieldResponses.value,
    })
  } catch (err) {
    console.error('Failed to generate waiver PDF', err)
  } finally {
    pdfGenerating.value = false
  }
}

async function toggleNotParticipating() {
  // Auto-save the not_participating toggle. Reverts the switch on failure.
  if (!selectedWaiver.value || !selectedWaiver.value.id) return
  const newValue = selectedWaiver.value.not_participating
  try {
    const config = await authConfig()
    const data = {
      booking_slot_id: selectedWaiver.value.booking_slot_id,
      not_participating: newValue,
    }
    await axios.put(waiverUrl(), data, config)
    proxy.$notify({
      type: 'success',
      message: newValue ? 'Marked as not participating' : 'Marked as participating',
    })
    emit('changed', { action: 'participation', waiverId: selectedWaiver.value.id, notParticipating: !!newValue })
  } catch (err) {
    console.log(err)
    // Revert the toggle on error
    selectedWaiver.value.not_participating = !newValue
    proxy.$notify({
      type: 'danger',
      message: 'Could not update. Please try again.',
    })
  }
}

function viewCustomerFromDrawer() {
  const waiver = selectedWaiver.value
  if (!waiver || !waiver.user_id) return
  const route = proxy.$router.resolve({ path: '/customers/details', query: { id: waiver.user_id } })
  window.open(route.href, '_blank')
}

function viewEventFromDrawer() {
  const waiver = selectedWaiver.value
  if (!waiver || !waiver.game_instance_id) return
  const route = proxy.$router.resolve({ path: '/games/history/instance', query: { id: waiver.game_instance_id } })
  window.open(route.href, '_blank')
}

function moveWaiverFromDrawer() {
  isCopyMode.value = false
  selectedBooking.value = ''
  drawerShowAllBookings.value = false
  current.value = pickerStartDate()
  drawerView.value = 'booking-selector'
  changeDate()
}

function copyWaiverFromDrawer() {
  isCopyMode.value = true
  selectedBooking.value = ''
  drawerShowAllBookings.value = false
  current.value = pickerStartDate()
  drawerView.value = 'booking-selector'
  changeDate()
}

// The picker opens on the booking's day, or for a waiver with no booking, the day it was signed.
function pickerStartDate() {
  const w = selectedWaiver.value
  if (w && w.booking_date) return String(w.booking_date).slice(0, 10)
  if (w && w.created_time && moment(w.created_time).isValid()) return moment(w.created_time).format('YYYY-MM-DD')
  return moment().format('YYYY-MM-DD')
}

function backToDetail() {
  drawerView.value = 'detail'
  isCopyMode.value = false
  drawerShowAllBookings.value = false
  selectedBooking.value = selectedWaiver.value && selectedWaiver.value.booking_slot_id
    ? { id: selectedWaiver.value.booking_slot_id }
    : ''
}

function closeDrawer() {
  drawerView.value = 'detail'
  isCopyMode.value = false
  drawerShowAllBookings.value = false
  selectedBooking.value = ''
  clearPrompt.show = false
  deletePrompt.show = false
  emit('close')
}

// Maps a waiver PUT/POST/DELETE failure to plain copy. The lambdas return plain strings.
function waiverActionError(err: any, action?: 'delete') {
  const status = err && err.response && err.response.status
  const body = err && err.response && err.response.data
  if (status === 401) return 'You do not have access to this action.'
  if (body === 'Not enabled') return 'Waivers are not enabled on your subscription plan.'
  if (body === 'Invalid booking slot') return 'That booking is not available. Pick another one.'
  if (body === 'Waiver is part of a completed game') {
    return action === 'delete'
      ? 'This waiver is part of a completed event and cannot be deleted.'
      : 'This waiver is part of a completed event and cannot be cleared.'
  }
  if (body === 'Waiver has no template') return 'This waiver can only live on a booking. Use Move to change its booking.'
  return 'Something went wrong. Please try again.'
}

async function clearWaiverBooking() {
  if (clearPrompt.loading || !selectedWaiver.value || !selectedWaiver.value.id) return
  const id = selectedWaiver.value.id
  clearPrompt.loading = true
  try {
    const config = await authConfig()
    await axios.put(waiverUrl(), { clear_booking: true }, config)
    clearPrompt.show = false
    // Let the dialog hand the scroll lock back while the drawer is still open (see deleteWaiver).
    await nextTick()
    proxy.$notify({ type: 'success', message: 'Booking cleared. The waiver is a walk-in.' })
    emit('changed', { action: 'cleared', waiverId: id })
    closeDrawer()
  } catch (err) {
    console.log(err)
    clearPrompt.show = false
    proxy.$notify({ type: 'danger', message: waiverActionError(err) })
  } finally {
    clearPrompt.loading = false
  }
}

async function updateWaiver() {
  if (saving.value || !selectedBooking.value || !selectedWaiver.value) return
  const wasWalkin = isWalkinWaiver.value
  const copying = isCopyMode.value
  const id = selectedWaiver.value.id
  saving.value = true
  try {
    const config = await authConfig()
    if (copying) {
      // Copy creates a second waiver on the chosen booking.
      await axios.post(waiverUrl(), { booking_slot_id: selectedBooking.value.id }, config)
    } else {
      // Move or assign updates this waiver in place.
      await axios.put(waiverUrl(), {
        booking_slot_id: selectedBooking.value.id,
        not_participating: selectedWaiver.value.not_participating,
      }, config)
    }
    proxy.$notify({
      type: 'success',
      message: copying ? 'Waiver copied to the booking' : (wasWalkin ? 'Waiver assigned to the booking' : 'Waiver moved to the booking'),
    })
    emit('changed', { action: copying ? 'copied' : (wasWalkin ? 'assigned' : 'moved'), waiverId: id })
    closeDrawer()
  } catch (err) {
    console.log(err)
    proxy.$notify({ type: 'danger', message: waiverActionError(err) })
  } finally {
    saving.value = false
  }
}

async function deleteWaiver() {
  if (deletePrompt.loading || !selectedWaiver.value || !selectedWaiver.value.id) return
  const id = selectedWaiver.value.id
  deletePrompt.loading = true
  try {
    const config = await authConfig()
    await axios.delete(waiverUrl(), config)
    deletePrompt.show = false
    // Let the dialog hand the scroll lock back while the drawer is still open: the dialog
    // restores the overflow it saved on open ('hidden', set by the drawer). If both close in
    // the same tick, BaseDrawer clears the lock first and the dialog then re-locks the page.
    await nextTick()
    proxy.$notify({ type: 'success', message: 'Waiver deleted' })
    emit('deleted', id)
    closeDrawer()
  } catch (err) {
    console.log(err)
    deletePrompt.show = false
    proxy.$notify({ type: 'danger', message: waiverActionError(err, 'delete') })
  } finally {
    deletePrompt.loading = false
  }
}

async function getBookings() {
  bookingsLoading.value = true
  try {
    const config = await authConfig()
    const response = await axios.get(groupUrl() + '/bookings?day=' + current.value + '&type=2', config)
    bookings.value = response.data.bookings || []
    emptyBooking.value = bookings.value.length <= 0
  } catch (err: any) {
    console.log(err)
    bookings.value = []
    proxy.$notify({
      type: 'danger',
      message: err && err.response && err.response.status === 401
        ? 'You do not have access to this action.'
        : 'Could not load bookings. Please try again.',
    })
  } finally {
    bookingsLoading.value = false
  }
}

function changeDate() {
  getBookings()
}

function onKeydown(e: KeyboardEvent) {
  // The alert dialogs handle their own ESC; never close the drawer underneath them.
  if (e.key !== 'Escape' || clearPrompt.show || deletePrompt.show) return
  closeDrawer()
}

watch(() => props.show, (open) => {
  if (open && props.waiver) {
    document.addEventListener('keydown', onKeydown)
    // Cast tinyint(1) to a true boolean for the el-switch v-model
    selectedWaiver.value = { ...props.waiver, not_participating: !!props.waiver.not_participating }
    selectedBooking.value = props.waiver.booking_slot_id ? { id: props.waiver.booking_slot_id } : ''
    customFieldResponses.value = []
    consentFields.value = []
    drawerView.value = 'detail'
    isCopyMode.value = false
    drawerShowAllBookings.value = false
    loadWaiverDetail(props.waiver.id)
  } else {
    document.removeEventListener('keydown', onKeydown)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
})
</script>

<script lang="ts">
// Element's dropdown parts aren't globally registered (the custom fields Copy menu uses them).
import { Dropdown, DropdownMenu, DropdownItem } from 'element-ui'

export default {
  name: 'waiver-drawer',
  components: {
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem
  }
}
</script>

<style scoped>
  .consent-question {
    color: rgba(255, 255, 255, 0.6);
    font-style: italic;
  }
  .white-content .consent-question {
    color: rgba(0, 0, 0, 0.55);
  }

  .drawer-back-link {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.65);
    cursor: pointer;
    margin-bottom: 12px;
  }
  .drawer-back-link:hover {
    color: #e14eca;
    text-decoration: none;
  }
  .drawer-back-link i {
    font-size: 12px;
  }

  /* Booking selector picker — mirrors tp-picker pattern in TakePhotos */
  .bsel-picker-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .bsel-picker-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 8px;
    cursor: pointer;
    min-height: 56px;
    transition: background 0.12s ease;
  }
  .bsel-picker-row:hover:not(.bsel-picker-row--selected) {
    background: rgba(255, 255, 255, 0.02);
  }
  .bsel-picker-row--selected {
    background: rgba(225, 78, 202, 0.08);
  }
  .bsel-picker-row__time {
    flex-shrink: 0;
    min-width: 44px;
    font-size: 13px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
  }
  .bsel-picker-event {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
    position: relative;
  }
  .bsel-picker-event__thumb-wrap {
    position: relative;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
  }
  .bsel-picker-event__img {
    width: 36px;
    height: 36px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    display: block;
  }
  .bsel-picker-event__img--placeholder {
    background: rgba(255, 255, 255, 0.04);
  }
  .bsel-picker-event__thumb-wrap.is-selected .bsel-picker-event__img {
    box-shadow: 0 0 0 2px #e14eca;
    border-color: transparent;
  }
  .bsel-picker-event__check {
    position: absolute;
    bottom: -5px;
    right: -5px;
    width: 18px;
    height: 18px;
    border-radius: 4px;
    background: #171826;
    border: 2px solid rgba(255, 255, 255, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.12s ease, background 0.12s ease, border-color 0.12s ease;
    box-sizing: border-box;
  }
  .bsel-picker-event__check i {
    font-size: 10px;
    color: transparent;
    line-height: 1;
  }
  .bsel-picker-event__check.is-checked {
    opacity: 1;
    background: #e14eca;
    border-color: #e14eca;
  }
  .bsel-picker-event__check.is-checked i {
    color: #fff;
  }
  .bsel-picker-row:hover .bsel-picker-event__check {
    opacity: 1;
  }
  @media (hover: none) {
    .bsel-picker-event__check {
      opacity: 0.5;
    }
    .bsel-picker-event__check.is-checked {
      opacity: 1;
    }
  }
  .bsel-picker-event__text {
    flex: 1;
    min-width: 0;
  }
  .bsel-picker-event__status {
    margin-left: auto;
    flex-shrink: 0;
    line-height: 1;
  }
  .bsel-show-all {
    display: flex;
    justify-content: center;
    padding: 8px 0 4px;
  }
  .bsel-show-all__btn {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.55);
    font-size: 12px;
    padding: 6px 10px;
    cursor: pointer;
    border-radius: 6px;
    transition: color 0.12s ease, background 0.12s ease;
  }
  .bsel-show-all__btn:hover {
    color: #e14eca;
    background: rgba(225, 78, 202, 0.08);
  }

  .white-content .drawer-back-link {
    color: rgba(0, 0, 0, 0.6);
  }

  /* Waiver detail drawer */
  .drawer-action-link {
    font-size: 12px;
    color: rgba(225, 78, 202, 0.85);
    cursor: pointer;
    white-space: nowrap;
  }
  .drawer-action-link:hover {
    color: #e14eca;
    text-decoration: none;
  }
  .drawer-action-link i {
    font-size: 9px;
    margin-left: 2px;
  }

  /* Download PDF: icon + label sized and colored as one unit */
  .pdf-download-link {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .pdf-download-link i {
    font-size: 13px;
    margin: 0;
  }
  .pdf-download-link.is-busy {
    opacity: 0.7;
    pointer-events: none;
  }

  .signature-block {
    margin-top: 10px;
    margin-bottom: 8px;
  }
  .signature-label {
    font-size: 12px;
    margin-bottom: 6px;
    opacity: 0.85;
  }
  .signature-label i {
    font-size: 11px;
  }

  .event-actions {
    display: flex;
    gap: 8px;
    margin-top: 8px;
  }

  /* Custom field responses — matches BookingDrawer .custom-field-item */
  .custom-fields-list {
    display: flex;
    flex-direction: column;
  }
  .custom-field-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px 0;
  }
  .custom-field-item + .custom-field-item {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
  .cf-label {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.55);
  }
  .cf-value {
    font-size: 13px;
    word-break: break-word;
    white-space: pre-wrap;
  }
  .cf-meta {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
  }
  .cf-meta-label {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.55);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 600;
  }
  .cf-count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 6px;
    border-radius: 9px;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.85);
    font-size: 11px;
    font-weight: 600;
  }
  .cf-copy-link {
    color: #e14eca;
    font-size: 12px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .cf-copy-link:hover {
    color: #b73ca5;
  }

  /* Light mode overrides for the waiver detail drawer */
  .white-content .drawer-action-link {
    color: #cc5fad;
  }
  .white-content .drawer-action-link:hover {
    color: #e14eca;
  }

  .white-content .custom-field-item + .custom-field-item {
    border-top-color: rgba(0, 0, 0, 0.08);
  }
  .white-content .cf-label,
  .white-content .cf-meta-label {
    color: rgba(0, 0, 0, 0.6);
  }
  .white-content .cf-count-badge {
    background: rgba(0, 0, 0, 0.08);
    color: rgba(0, 0, 0, 0.75);
  }
  .white-content .cf-copy-link {
    color: #cc5fad;
  }
  .white-content .cf-copy-link:hover {
    color: #e14eca;
  }
</style>
